import type { InteractionItemType } from "@gerama/api/core";
import { browserCoreApi } from "@/lib/api";

interface Status {
  liked: boolean;
  saved: boolean;
}
interface Waiter {
  id: number;
  resolve: (status: Status) => void;
  reject: (error: unknown) => void;
}

const WINDOW_MS = 30;
const MAX_IDS = 200;
const queues = new Map<InteractionItemType, Waiter[]>();

async function flush(itemType: InteractionItemType) {
  const waiters = queues.get(itemType) ?? [];
  queues.delete(itemType);
  if (waiters.length === 0) return;
  const ids = [...new Set(waiters.map((w) => w.id))];
  try {
    // A long page is split so one request never exceeds the endpoint's cap.
    const chunks: number[][] = [];
    for (let i = 0; i < ids.length; i += MAX_IDS) chunks.push(ids.slice(i, i + MAX_IDS));
    const results = await Promise.all(
      chunks.map((chunk) => browserCoreApi.playback.statusBatch(itemType, chunk)),
    );
    const liked = new Set(results.flatMap((r) => r.liked));
    const saved = new Set(results.flatMap((r) => r.saved));
    for (const waiter of waiters)
      waiter.resolve({ liked: liked.has(waiter.id), saved: saved.has(waiter.id) });
  } catch (error) {
    for (const waiter of waiters) waiter.reject(error);
  }
}

/**
 * The liked/saved state of one item, fetched together with every other item asked
 * about in the same ~30 ms: a list of 30 rows with hearts makes one request, not 30.
 */
export function loadInteractionStatus(itemType: InteractionItemType, id: number): Promise<Status> {
  return new Promise((resolve, reject) => {
    const queue = queues.get(itemType);
    if (queue) queue.push({ id, resolve, reject });
    else {
      queues.set(itemType, [{ id, resolve, reject }]);
      setTimeout(() => void flush(itemType), WINDOW_MS);
    }
  });
}
